import { useId, useMemo, useState, type FormEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { Link } from 'react-router';
import { fhir } from '@curentia/shared';
import { ErrorText, Field, Submit } from '../components';
import { formatDate, formatDateTime, localInputValue, type ReadingPoint, type TimelineItem } from '../lib/health';
import { useAction } from '../state';

const { READING_KINDS, READING_LABELS, READING_UNITS, celsiusFromFahrenheit, readingProblem, readingText, readingValues } = fhir;
type ReadingKind = fhir.ReadingKind;

/** Documents newest first: what each says, when, and where it came from. linkFor opens a document, such as a prescription to print. */
export function Timeline({
  items,
  empty,
  label = 'Timeline',
  linkFor,
}: {
  items: TimelineItem[];
  empty: string;
  label?: string;
  linkFor?: (item: TimelineItem) => string | null;
}) {
  if (items.length === 0) return <p className="muted">{empty}</p>;
  return (
    <ol className="timeline" aria-label={label}>
      {items.map((item) => (
        <li key={item.id}>
          <time dateTime={item.date}>{formatDate(item.date)}</time>
          <div className="timeline-body">
            {linkFor?.(item) ? (
              <Link to={linkFor(item) ?? ''} className="timeline-link">
                {item.title}
              </Link>
            ) : (
              <strong>{item.title}</strong>
            )}
            {item.lines.length > 0 ? (
              <ul>
                {item.lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : null}
            <span className="muted">{item.byline}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---- The readings chart ------------------------------------------------------------

const WIDTH = 328;
const HEIGHT = 196;
const MARGIN = { top: 14, right: 44, bottom: 28, left: 38 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const HOUR = 3_600_000;

interface Series {
  name: string;
  slot: 1 | 2;
  values: number[];
}

/** Round tick values covering the range, about four of them. */
function niceTicks(min: number, max: number): number[] {
  const raw = (max - min) / 4 || 1;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * power >= raw) ?? 10) * power;
  const ticks: number[] = [];
  for (let v = Math.floor(min / step) * step; v <= max + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

function seriesOf(kind: ReadingKind, points: ReadingPoint[]): Series[] {
  if (kind === 'blood_pressure') {
    return [
      { name: 'Systolic (upper)', slot: 1, values: points.map((p) => readingValues(p.reading)[0] ?? 0) },
      { name: 'Diastolic (lower)', slot: 2, values: points.map((p) => readingValues(p.reading)[1] ?? 0) },
    ];
  }
  return [{ name: READING_LABELS[kind], slot: 1, values: points.map((p) => readingValues(p.reading)[0] ?? 0) }];
}

const short = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

/**
 * One kind of reading over time: a line per value, one y axis, a crosshair
 * and readout on hover or keyboard focus. The list under the chart is its
 * table view, so no value is only in the chart.
 */
export function ReadingsChart({ kind, points }: { kind: ReadingKind; points: ReadingPoint[] }) {
  const [active, setActive] = useState<number | null>(null);
  const titleId = useId();
  const series = useMemo(() => seriesOf(kind, points), [kind, points]);
  const times = points.map((p) => Date.parse(p.reading.at));
  const first = Math.min(...times);
  const last = Math.max(...times);
  const [t0, t1] = first === last ? [first - 12 * HOUR, last + 12 * HOUR] : [first, last];
  const all = series.flatMap((s) => s.values);
  const spread = Math.max(...all) - Math.min(...all);
  const pad = Math.max(spread * 0.15, kind === 'temperature' ? 0.3 : 2);
  const ticks = niceTicks(Math.min(...all) - pad, Math.max(...all) + pad);
  const [y0, y1] = [ticks[0] ?? 0, ticks.at(-1) ?? 1];
  const x = (t: number) => MARGIN.left + ((t - t0) / (t1 - t0)) * PLOT_W;
  const y = (v: number) => MARGIN.top + (1 - (v - y0) / (y1 - y0 || 1)) * PLOT_H;
  const unit = READING_UNITS[kind];
  const latest = points.at(-1);

  const nearest = (clientX: number, box: DOMRect) => {
    const t = t0 + ((((clientX - box.left) / box.width) * WIDTH - MARGIN.left) / PLOT_W) * (t1 - t0);
    let best = 0;
    times.forEach((time, i) => {
      if (Math.abs(time - t) < Math.abs((times[best] ?? 0) - t)) best = i;
    });
    return best;
  };
  const onPointer = (event: PointerEvent<SVGRectElement>) => setActive(nearest(event.clientX, event.currentTarget.ownerSVGElement?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect()));
  const onKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const from = active ?? points.length - 1;
    setActive(Math.min(points.length - 1, Math.max(0, from + (event.key === 'ArrowLeft' ? -1 : 1))));
  };

  // End labels sit beside each line's last point, unless two would collide.
  const ends = series.map((s) => ({ s, value: s.values.at(-1) ?? 0 }));
  const collide = ends.length === 2 && Math.abs(y(ends[0]?.value ?? 0) - y(ends[1]?.value ?? 0)) < 14;
  const shown = active === null ? null : points[active];
  const xLabels = first === last ? [first] : last - first > 2 * 24 * HOUR ? [first, (first + last) / 2, last] : [first, last];

  return (
    <figure className="chart" aria-labelledby={titleId}>
      <figcaption id={titleId} className="sr-only">
        {READING_LABELS[kind]}: {points.length} {points.length === 1 ? 'reading' : 'readings'}, latest {latest ? readingText(latest.reading) : ''}. The list below has every value. Use the arrow keys to step through them.
      </figcaption>
      {series.length > 1 ? (
        <div className="chart-legend" aria-hidden="true">
          {series.map((s) => (
            <span key={s.name}>
              <i className={`key series-${s.slot}`} />
              {s.name}
            </span>
          ))}
        </div>
      ) : null}
      <div
        className="chart-frame"
        tabIndex={0}
        onKeyDown={onKey}
        // A tap focuses the chart after it picked a point; keep that point rather than jumping to the latest.
        onFocus={() => setActive((current) => current ?? points.length - 1)}
        onBlur={() => setActive(null)}
        aria-describedby={titleId}
        role="group"
        aria-roledescription="chart"
      >
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} aria-hidden="true">
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="grid" x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(tick)} y2={y(tick)} />
              <text className="axis-label" x={MARGIN.left - 6} y={y(tick) + 4} textAnchor="end">
                {tick}
              </text>
            </g>
          ))}
          {xLabels.map((t, i) => (
            <text key={t} className="axis-label" x={x(t)} y={HEIGHT - 8} textAnchor={xLabels.length === 1 ? 'middle' : i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}>
              {short(new Date(t).toISOString())}
            </text>
          ))}
          {shown ? <line className="crosshair" x1={x(Date.parse(shown.reading.at))} x2={x(Date.parse(shown.reading.at))} y1={MARGIN.top} y2={MARGIN.top + PLOT_H} /> : null}
          {series.map((s) => (
            <g key={s.name} className={`series-${s.slot}`}>
              {points.length > 1 ? <polyline className="line" points={s.values.map((v, i) => `${x(times[i] ?? 0)},${y(v)}`).join(' ')} /> : null}
              {points.length <= 40 || active !== null
                ? s.values.map((v, i) => (points.length <= 40 || i === active ? <circle key={i} className="marker" cx={x(times[i] ?? 0)} cy={y(v)} r={i === active ? 5.5 : 4} /> : null))
                : null}
            </g>
          ))}
          {!collide
            ? ends.map(({ s, value }) => (
                <text key={s.name} className="end-label" x={x(last) + 9} y={y(value) + 4}>
                  {value}
                </text>
              ))
            : null}
          <rect
            className="hit"
            x={MARGIN.left}
            y={0}
            width={PLOT_W}
            height={HEIGHT}
            onPointerMove={onPointer}
            onPointerDown={onPointer}
            // A finger lifting also counts as leaving; only a mouse moving away hides the readout.
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') setActive(null);
            }}
          />
        </svg>
        {shown ? (
          <div className="chart-tooltip" style={{ left: `${(x(Date.parse(shown.reading.at)) / WIDTH) * 100}%` }} data-flip={x(Date.parse(shown.reading.at)) > WIDTH * 0.6 ? 'left' : 'right'}>
            {series.map((s) => (
              <div key={s.name} className="tooltip-row">
                <i className={`key series-${s.slot}`} />
                <strong>
                  {s.values[active ?? 0]} {unit === 'beats a minute' ? 'bpm' : unit}
                </strong>
                {series.length > 1 ? <span>{s.name.split(' ')[0]}</span> : null}
              </div>
            ))}
            <span className="muted">{formatDateTime(shown.reading.at)}</span>
          </div>
        ) : null}
      </div>
    </figure>
  );
}

// ---- Adding a reading ------------------------------------------------------------------

const FORM_START = { systolic: '', diastolic: '', value: '', context: 'random', unit: 'F', at: '' };

function readingFrom(kind: ReadingKind, form: typeof FORM_START): fhir.Reading | string {
  const at = form.at ? new Date(form.at).toISOString() : new Date().toISOString();
  const number = (text: string) => (text.trim() === '' ? Number.NaN : Number(text));
  const value = number(form.value);
  let reading: fhir.Reading;
  switch (kind) {
    case 'blood_pressure':
      reading = { kind, at, systolic: number(form.systolic), diastolic: number(form.diastolic) };
      break;
    case 'blood_glucose':
      reading = { kind, at, mgPerDl: value, context: form.context as 'fasting' | 'after_meal' | 'random' };
      break;
    case 'temperature':
      reading = { kind, at, celsius: form.unit === 'F' ? celsiusFromFahrenheit(value) : value };
      break;
    case 'weight':
      reading = { kind, at, kg: value };
      break;
    case 'pulse':
      reading = { kind, at, bpm: value };
      break;
    case 'spo2':
      reading = { kind, at, percent: value };
      break;
  }
  return readingProblem(reading) ?? reading;
}

/** A form for any of the six readings. Blood pressure comes first, as it is the most common. */
export function ReadingForm({ onSave }: { onSave(reading: fhir.Reading): Promise<void> }) {
  const [kind, setKind] = useState<ReadingKind>('blood_pressure');
  const [form, setForm] = useState(FORM_START);
  const { busy, error, setError, run } = useAction();
  const kindId = useId();
  const optionId = useId();
  const set = (key: keyof typeof FORM_START) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const reading = readingFrom(kind, form);
    if (typeof reading === 'string') return setError(reading);
    void run(async () => {
      await onSave(reading);
      setForm({ ...FORM_START, unit: form.unit });
    });
  };

  const single = kind === 'weight' || kind === 'pulse' || kind === 'spo2';
  return (
    <form onSubmit={submit} noValidate className="reading-form">
      <h3>Add a reading</h3>
      <div className="field">
        <label htmlFor={kindId}>Kind of reading</label>
        <select id={kindId} value={kind} onChange={(e) => setKind(e.target.value as ReadingKind)}>
          {READING_KINDS.map((k) => (
            <option key={k} value={k}>
              {READING_LABELS[k]}
            </option>
          ))}
        </select>
      </div>
      {kind === 'blood_pressure' ? (
        <div className="pair">
          <Field label="Systolic (upper)" inputMode="numeric" value={form.systolic} onChange={set('systolic')} />
          <Field label="Diastolic (lower)" inputMode="numeric" value={form.diastolic} onChange={set('diastolic')} />
        </div>
      ) : null}
      {kind === 'blood_glucose' ? (
        <div className="pair">
          <Field label="Blood sugar (mg/dL)" inputMode="numeric" value={form.value} onChange={set('value')} />
          <div className="field">
            <label htmlFor={optionId}>When</label>
            <select id={optionId} value={form.context} onChange={set('context')}>
              <option value="fasting">Fasting</option>
              <option value="after_meal">After a meal</option>
              <option value="random">Any time</option>
            </select>
          </div>
        </div>
      ) : null}
      {kind === 'temperature' ? (
        <div className="pair">
          <Field label="Temperature" inputMode="decimal" value={form.value} onChange={set('value')} />
          <div className="field">
            <label htmlFor={optionId}>Scale</label>
            <select id={optionId} value={form.unit} onChange={set('unit')}>
              <option value="F">°F</option>
              <option value="C">°C</option>
            </select>
          </div>
        </div>
      ) : null}
      {single ? (
        <Field
          label={`${READING_LABELS[kind]} (${kind === 'pulse' ? 'beats a minute' : READING_UNITS[kind]})`}
          inputMode={kind === 'weight' ? 'decimal' : 'numeric'}
          value={form.value}
          onChange={set('value')}
        />
      ) : null}
      <Field label="Taken at" type="datetime-local" value={form.at || localInputValue(new Date())} max={localInputValue(new Date())} onChange={set('at')} />
      <ErrorText error={error} />
      <Submit busy={busy} busyText="Saving…">
        Save the reading
      </Submit>
    </form>
  );
}

/**
 * Readings of one kind at a time: a chart, the list of values that is the
 * chart's table view, and the form to add one.
 */
export function ReadingsPanel({
  points,
  onAdd,
  heading = 'Readings',
  here,
}: {
  points: ReadingPoint[];
  onAdd?: (reading: fhir.Reading) => Promise<void>;
  heading?: string;
  /** Where this device's own readings come from, left out of the list to keep it short. */
  here: string;
}) {
  const present = READING_KINDS.filter((k) => points.some((p) => p.reading.kind === k));
  const [chosen, setChosen] = useState<ReadingKind | null>(null);
  const [all, setAll] = useState(false);
  const kind = chosen && present.includes(chosen) ? chosen : (present[0] ?? 'blood_pressure');
  const shown = points.filter((p) => p.reading.kind === kind);
  const newest = [...shown].reverse();
  const listed = all ? newest : newest.slice(0, 8);
  const headingId = useId();
  return (
    <section className="card" aria-labelledby={headingId}>
      <h2 id={headingId}>{heading}</h2>
      {present.length > 1 ? (
        <div className="segmented" role="group" aria-label="Show">
          {present.map((k) => (
            <button key={k} type="button" aria-pressed={k === kind} onClick={() => setChosen(k)}>
              {READING_LABELS[k]}
            </button>
          ))}
        </div>
      ) : null}
      {shown.length === 0 ? <p className="muted">No readings yet.</p> : <ReadingsChart kind={kind} points={shown} />}
      {listed.length > 0 ? (
        <ul className="list readings" aria-label="Readings">
          {listed.map((point) => (
            <li key={point.id} className="member">
              <strong>{readingText(point.reading)}</strong>
              <span className="muted">
                {formatDateTime(point.reading.at)}
                {point.from !== here ? ` · from ${point.from}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {newest.length > listed.length ? (
        <button type="button" className="button secondary small" onClick={() => setAll(true)}>
          Show all {newest.length} readings
        </button>
      ) : null}
      {onAdd ? <ReadingForm onSave={onAdd} /> : null}
    </section>
  );
}
